import {useLingui} from '@lingui/react/macro';
import {Fragment} from 'react';
import isEqual from 'react-fast-compare';
import {Svg, Path} from 'react-native-svg';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate, useOutletContext} from 'react-router';
import {useCallback, useState, createRef, memo} from 'react';
import {useWindowDimensions, View, StyleSheet, TextInput, Pressable, Text,/*, StatusBar*/} from 'react-native';
import {SafeAreaView} from 'extensions/viewport/SafeArea';
import {IconButton} from 'interface/base/IconButton';
import {useCopyLink} from 'interface/hooks/useCopyLink';
import {useMenuFiles} from 'interface/hooks/useMenuFiles';
import {useMenuDisplay} from 'interface/hooks/useMenuDisplay';
import {useMenuSort} from 'interface/hooks/useMenuSort';
import {useMenuFilter} from 'interface/hooks/useMenuFilter';
import {useModalSort} from 'interface/hooks/useModalSort';
import {useModalFilter} from 'interface/hooks/useModalFilter';
import {useModalShare} from 'interface/hooks/useModalShare';
import {useModalPurge} from 'interface/hooks/useModalPurge';
import {useModalCreateFolder} from 'interface/hooks/useModalCreateFolder';
import {useModalBulkDownload} from 'interface/hooks/useModalBulkDownload';
import {useModalUpsellFeature} from 'interface/hooks/useModalUpsellFeature';
import {usePopup} from 'extensions/viewport/usePopup';
import {FileListItemLayout} from 'interface/stacks/FileListItem';
import {openPage, isFolderkey, isQuickkey, getShareLink, isFolderSpecial} from 'utils/mediafire';
import {getHost, isTouch} from 'utils/platform';
import {luminance} from 'utils/common/color';
import {testBit} from 'utils/common/data';
import {theme} from 'config/themes';
import {colors, breakpoints, vectors} from 'config/styles';
import {FilesItemFlag} from 'store/slices/files/types';
import {selectors} from 'store/slices/files';

import * as app from 'store/slices/app';
import * as transfers from 'store/slices/transfers';
import * as effectsFiles from 'interface/hooks/effects/files';
import * as effectsTransfers from 'interface/hooks/effects/transfers';
import {LayoutSelector} from 'interface/stacks/ThumbnailSizeSelector';
import {PopupMenu} from 'extensions/viewport/PopupMenu';

export const filesBarHeight = 65;

export interface FolderToolbarProps {
  id: string,
  loaded: boolean,
}

export const FolderToolbar = memo((props: FolderToolbarProps) => {
  const {t} = useLingui();
  const [stateSearch, setSearch] = useState('');
  const [openMenu] = useOutletContext<any>();
  const screen = useWindowDimensions();
  const header = useSelector(app.selectors.getHeader);
  const isPremium = useSelector(app.selectors.isPremium);
  const isLoggedIn = useSelector(app.selectors.isLoggedIn);
  const isSearch = useSelector(selectors.isSearch);
  const trash = useSelector(selectors.getView('trash'));
  const selection = useSelector(selectors.getSelection);
  const selectItem = useSelector(selectors.getItem(selection[0]));
  const hasPrivate = useSelector(selectors.hasPrivateItems(selection));
  const folder = useSelector(selectors.getItem(props.id));
  const upload = useSelector(transfers.selectors.getUpload);
  const host = getHost();
  const itemUrl = selectItem ? getShareLink(selectItem.id, selectItem.type, selectItem.name) : '';
  const isTrash = props.id === 'trash';
  const isRecent = props.id === 'recent';
  const isFollowing = props.id === 'following';
  const isLimited = isRecent || isFollowing;
  const isSpecial = isFolderSpecial(props.id);
  const isOwned = folder && testBit(folder.flag, FilesItemFlag.isOwned);
  const isShared = !isSpecial && folder && !isOwned;
  const isModeSelect = selection.length > 0;
  const isModeSearch = !isModeSelect && isSearch;
  const isModeTrash = !isModeSelect && isTrash;
  const isModeShared = !isModeSelect && !isModeTrash && isShared;
  const isModeNormal = !isModeSelect && !isModeSearch && !isModeShared && !isModeTrash;
  const hasCustomLogo = header && !!header.logo;
  const hasCustomHeader = header && header.custom;
  const trashIds = trash ? [...trash.files, ...trash.folders] : [];
  const folders = selection.filter(id => isFolderkey(id));
  const files = selection.filter(id => isQuickkey(id));
  const hasFolders = folders.length > 0;
  const hasFiles = files.length > 0;
  const hasMixed = hasFiles && hasFolders;
  const hasMultiple = selection.length > 1;
  const hasFolder = isFolderkey(selection[0]);
  const hasMenu = openMenu && screen.width <= breakpoints.menu;
  const hasLogo = screen.width >= 320;
  const hasFullLogo = !isModeSelect && screen.width >= 1024;
  const hasByline = hasFullLogo && screen.width >= 1280;
  const hasFullText = screen.width >= 860;
  const hasCopyLink = screen.width >= 400 && !hasPrivate;
  const hasSelected = screen.width >= 320;
  const hasDisplayMenu = screen.width < 615;
  const isLight = header && header.theme.header === 'light';
  const opacityButton = !hasCustomHeader ? 1.0 : 0.6;
  const opacityLogo = (!hasCustomHeader && !isModeSelect) || isModeSelect || isModeTrash ? 1.0 : 0.6;
  const iconColor = hasCustomHeader ? isLight ? colors.neutral.white : colors.neutral.black : colors.primary.light;
  const iconColorAlpha = hasCustomHeader ? isLight ? `rgba(255,255,255,0.4)` : `rgba(0,0,0,0.4)` : `rgba(255,255,255,0.4)`;
  const iconSelectOpacity = 1;
  const iconSelectColor = colors.neutral.white;
  const iconColorLogoText = isModeShared
    ? colors.primary.dark
    : isModeTrash
      ? colors.neutral.white
      : isModeSelect
        ? colors.neutral.white
        : hasCustomHeader
          ? isLight
            ? colors.neutral.white
            : colors.neutral.black
          : colors.neutral.white;
  const iconColorLogoFlame = isModeShared ? theme.app.logo : iconColorLogoText;
  const primaryColor = header ? header.colors.primary : theme.app.logo;
  const headerColor = hasCustomHeader ? header.colors.header : theme.app.logo;
  const barColor = isModeTrash
    ? colors.secondary.redOrange
    : isModeSelect
      ? colors.primary.dark
      : headerColor;
  const hoverStyle = {
    backgroundColor: luminance(barColor, -0.2),
  };
  const classes = {
    root: [
      styles.root,
      hasCustomHeader && {backgroundColor: headerColor},
      isModeShared && styles.rootShared,
      isModeTrash && styles.rootTrash,
      isModeSelect && styles.rootSelect,
      isModeSelect && isShared && styles.rootSharedSelect,
    ],
    buttonText: [
      styles.buttonText,
      isModeSelect && styles.buttonTextSelect,
    ],
    signup: [
      styles.primary,
      hasCustomHeader && {backgroundColor: headerColor},
    ],
    searchInput: [
      styles.searchInput,
      {color: iconColor, backgroundColor: headerColor},
    ],
  };

  const url = hasMultiple ? `${getHost(true)}/folder/${selection.join(',')}/shared` : itemUrl;
  const name = hasMultiple ? t`Multiple Items` : selectItem?.name;

  // const barMode = isModeTrash || isModeSelect || !hasCustomHeader || isLight
  //  ? 'light-content'
  //  : 'dark-content';

  // Actions
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const myfiles = useCallback(() => navigate('/folder/myfiles'), []);
  const copy = useCopyLink(selection, selectItem ? selectItem.url : '');
  const restore = useCallback(effectsFiles.pick(dispatch, {action: 'restore', destination: 'myfiles', ids: selection.length > 0 ? selection : trashIds}), [selection, trashIds]);
  const download = useCallback(effectsFiles.downloadFile(itemUrl), [itemUrl]);
  const selectClear = useCallback(effectsFiles.selectClear(dispatch, null), []);
  const searchOpen = useCallback(effectsFiles.search(navigate, dispatch, 'myfiles'), []);
  const searchSubmit = useCallback(effectsFiles.search(navigate, dispatch, 'myfiles', stateSearch), [stateSearch]);
  const searchClose = useCallback(effectsFiles.search(navigate, dispatch, null), []);
  const uploadStart = useCallback(effectsTransfers.uploadStart(dispatch, props.id), [props.id]);
  const uploadMaximize = useCallback(effectsTransfers.uploadMinimize(dispatch, false), []);

  const popup = usePopup();
  const filesRef = createRef<any>();
  const displayRef = createRef<any>();
  const sortRef = createRef<any>();
  const filterRef = createRef<any>();

  const showMenu = (
    ref: React.RefObject<any>,
    menu: {open: () => void; exit: () => void; reference: React.RefObject<any>},
  ) => {
    if (popup) {
      popup.setPosition(undefined);
      popup.setRef(ref);
    }
    menu.open();
  }

  // Modals
  const modalSort = useModalSort(props.id);
  const modalFilter = useModalFilter(props.id);
  const modalCreateFolder = useModalCreateFolder(props.id);
  const modalBulkDownload = useModalBulkDownload(selection);
  const modalPurge = useModalPurge(selection.length > 0 ? selection : trashIds);
  const [showShare] = useModalShare(selection, name, url, undefined, hasPrivate);
  const [showUpsellBulkDownload] = useModalUpsellFeature('pro', 'bulkdownload');

  // Menus
  const menuFiles = useMenuFiles(selection, selectItem, {isPremium, hasPrivate, primaryColor});
  const menuSort = useMenuSort(props.id);
  const menuFilter = useMenuFilter(props.id);
  const menuDisplay = useMenuDisplay(props.id);

  return (
    <SafeAreaView style={{backgroundColor: barColor}}>
      {/*<StatusBar barStyle={barMode} backgroundColor={barColor}/>*/}
      <View style={classes.root}>
        {hasLogo && !isModeSearch &&
          <Pressable style={styles.logo} onPress={myfiles}>
            {!hasCustomLogo && hasFullLogo &&
              <View style={styles.brand}>
                <Svg viewBox="0 0 291 41" width={168} height={24}>
                  <Path d={vectors.logo.flame} fill={iconColorLogoFlame} fillOpacity={opacityLogo}/>
                  {vectors.logo.letters.map((d,i) =>
                    <Path key={i} d={d} fill={iconColorLogoText} fillOpacity={opacityLogo}/>
                  )}
                </Svg>
                {hasByline &&
                  <Text style={[styles.brandBy, {color: iconColorLogoText}]}>by VividEngine</Text>
                }
              </View>
            }
            {!hasCustomLogo && !hasFullLogo &&
              <Svg viewBox="0 0 75 41" width={44} height={24}>
                <Path d={vectors.logo.flame} fill={iconColorLogoFlame} fillOpacity={opacityLogo}/>
              </Svg>
            }
            {hasCustomLogo &&
              <div style={{
                width: '190px',
                height: '65px',
                marginLeft: '12px',
                backgroundPosition: '0 center',
                backgroundRepeat: 'no-repeat',
                backgroundImage: `url(${header.logo})`,
              }}/>
            }
          </Pressable>
        }
        {props.loaded && <>
          {isModeNormal && isLoggedIn &&
            <View style={styles.actions}>
              <View style={styles.spacer}/>
              {!isLimited &&
                <Fragment>
                  <IconButton
                    onPress={upload ? uploadMaximize : uploadStart}
                    title={t`Upload files`}
                    text={hasFullText ? t`Upload`.toUpperCase() : ''}
                    textStyle={classes.buttonText}
                    buttonStyle={styles.button}
                    buttonHoverStyle={hoverStyle}
                    icon={
                      <Svg viewBox="0 0 14 17" width={14} height={17}>
                        <Path d={vectors.icons.upload} fill={iconColor} fillOpacity={opacityButton}/>
                      </Svg>
                    }
                  />
                  {!hasDisplayMenu && <>
                    <IconButton
                      reference={sortRef}
                      onPress={!isTouch() ? () => showMenu(sortRef, menuSort) : modalSort.open}
                      title={t`Sort folder`}
                      text={hasFullText ? t`Sort`.toUpperCase() : ''}
                      textStyle={classes.buttonText}
                      buttonStyle={styles.button}
                      buttonHoverStyle={hoverStyle}
                      icon={
                        <Svg ref={menuSort.reference} viewBox="0 0 20 20" width={20} height={20}>
                          <Path d={vectors.icons.sortAlt} fill={iconColor} fillOpacity={opacityButton}/>
                        </Svg>
                      }
                    />
                    <IconButton
                      reference={filterRef}
                      onPress={!isTouch() ? () => showMenu(filterRef, menuFilter) : modalFilter.open}
                      title={t`Filter items`}
                      text={hasFullText ? t`Filter`.toUpperCase() : ''}
                      textStyle={classes.buttonText}
                      buttonStyle={styles.button}
                      buttonHoverStyle={hoverStyle}
                      icon={
                        <Svg ref={menuFilter.reference} viewBox="0 0 24 24" width={24} height={24}>
                          <Path d={vectors.icons.filter} fill={iconColor} fillOpacity={opacityButton}/>
                        </Svg>
                      }
                    />
                  </>}
                  {hasDisplayMenu && 
                    <IconButton
                      reference={displayRef}
                      onPress={() => showMenu(displayRef, menuDisplay)}
                      title={t`Display settings`}
                      text={hasFullText ? t`Display`.toUpperCase() : ''}
                      textStyle={classes.buttonText}
                      buttonStyle={styles.button}
                      buttonHoverStyle={hoverStyle}
                      icon={
                        <Svg ref={menuDisplay.reference} viewBox="0 0 48 48" width={24} height={24}>
                          <Path d={vectors.icons.tune} fill={iconColor} fillOpacity={opacityButton}/>
                        </Svg>
                      }
                    />
                  }
                  <IconButton
                    onPress={searchOpen}
                    title={t`Search files`}
                    textStyle={classes.buttonText}
                    buttonStyle={styles.button}
                    buttonHoverStyle={hoverStyle}
                    icon={
                      <Svg viewBox="0 0 18 18" width={18} height={18}>
                        <Path d={vectors.icons.search} fill={iconColor} fillOpacity={opacityButton}/>
                      </Svg>
                    }
                  />
                  <IconButton
                    onPress={modalCreateFolder.open}
                    title={t`Create folder`}
                    buttonStyle={styles.button}
                    buttonHoverStyle={hoverStyle}
                    icon={
                      <Svg viewBox="0 0 20 16" width={20} height={16}>
                        <Path d={vectors.icons.folderAdd} fill={iconColor} fillOpacity={opacityButton}/>
                      </Svg>
                    }
                  />
                  {!hasDisplayMenu &&
                    <LayoutSelector />
                  }
                </Fragment>
              }
              {hasMenu &&
                <IconButton
                  onPress={openMenu}
                  title={t`Open main menu`}
                  buttonStyle={styles.button}
                  buttonHoverStyle={hoverStyle}
                  icon={
                    <Svg viewBox="0 0 18 12" width={18} height={12}>
                      <Path d={vectors.icons.menu} fill={iconColor} fillOpacity={opacityButton}/>
                    </Svg>
                  }
                />
              }
            </View>
          }
          {isModeSearch &&
            <View style={styles.actions}>
              <View style={styles.searchIcon}>
                <Svg viewBox="0 0 20 20" width={20} height={20}>
                  <Path d={vectors.icons.search} fill={iconColor} fillOpacity={opacityButton}/>
                </Svg>
              </View>
              <View style={styles.search}>
                <TextInput
                  autoFocus
                  spellCheck
                  style={classes.searchInput}
                  onChangeText={setSearch}
                  onSubmitEditing={searchSubmit}
                  placeholder={t`Search files & folders`}
                  placeholderTextColor={iconColorAlpha}
                  returnKeyType="search"
                  autoCorrect={false}
                  maxLength={40}
                />
              </View>
              <IconButton
                onPress={searchClose}
                title={t`Clear search`}
                buttonStyle={styles.button}
                buttonHoverStyle={hoverStyle}
                icon={
                  <Svg viewBox="0 0 14 14" width={14} height={14}>
                    <Path d={vectors.icons.close} fill={iconColor} fillOpacity={opacityButton}/>
                  </Svg>
                }
              />
            </View>
          }
          {isModeSelect &&
            <View style={styles.actions}>
            {hasSelected &&
              <Pressable ref={filesRef} onPress={() => showMenu(filesRef, menuFiles)}>
                <View style={styles.selected} ref={menuFiles.reference}>
                  <Text style={styles.selectedText}>
                    {hasFullText
                      ? t`${selection.length} ${hasMultiple ? 'items' : 'item'} selected`
                      : selection.length}
                  </Text>
                  <View style={styles.spacer}/>
                  <Svg viewBox="0 0 24 24" width={18} height={16}>
                    <Path
                      d={vectors.icons.dropDown}
                      fill={iconSelectColor}
                      fillOpacity={0.4}
                    />
                  </Svg>
                </View>
              </Pressable>
            }
            <View style={styles.spacer}/>
            {!isTrash && hasCopyLink &&
              <IconButton
                onPress={copy}
                title={t`Copy selection link`}
                text={hasFullText ? t`Copy link`.toUpperCase() : ''}
                textStyle={classes.buttonText}
                buttonStyle={styles.button}
                buttonHoverStyle={hoverStyle}
                icon={
                  <Svg viewBox="0 0 20 10" width={18} height={18}>
                    <Path d={vectors.icons.public} fill={iconSelectColor} fillOpacity={iconSelectOpacity}/>
                  </Svg>
                }
              />
            }
            {!hasMixed && !isTrash &&
              <IconButton
                onPress={showShare}
                title={t`Share selection`}
                text={hasFullText ? t`Share`.toUpperCase() : ''}
                textStyle={classes.buttonText}
                buttonStyle={styles.button}
                buttonHoverStyle={hoverStyle}
                icon={
                  <Svg viewBox="0 0 20 20" width={18} height={16}>
                    <Path d={vectors.icons.share} fill={iconSelectColor} fillOpacity={iconSelectOpacity}/>
                  </Svg>
                }
              />
            }
            {!isTrash &&
              <IconButton
                onPress={hasMultiple || hasFolder
                  ? isPremium
                    ? modalBulkDownload.open
                    : showUpsellBulkDownload
                  : download}
                title={t`Download selection`}
                text={hasFullText ? t`Download`.toUpperCase() : ''}
                textStyle={classes.buttonText}
                buttonStyle={styles.button}
                buttonHoverStyle={hoverStyle}
                icon={
                  <Svg viewBox="0 0 14 17" width={16} height={16}>
                    <Path d={vectors.icons.download} fill={iconSelectColor} fillOpacity={iconSelectOpacity}/>
                  </Svg>
                }
              />
            }
            {isTrash &&
              <IconButton
                onPress={modalPurge.open}
                title={t`Delete selection`}
                text={hasFullText ? t`Delete`.toUpperCase() : ''}
                textStyle={classes.buttonText}
                buttonStyle={styles.button}
                buttonHoverStyle={hoverStyle}
                icon={
                  <Svg viewBox="0 0 24 24" width={24} height={24}>
                    <Path d={vectors.icons.emptyTrash} fill={iconSelectColor} fillOpacity={iconSelectOpacity}/>
                  </Svg>
                }
              />
            }
            {isTrash &&
              <IconButton
                onPress={restore}
                title={t`Restore selection`}
                text={hasFullText ? t`Restore`.toUpperCase() : ''}
                textStyle={classes.buttonText}
                buttonStyle={styles.button}
                buttonHoverStyle={hoverStyle}
                icon={
                  <Svg viewBox="0 0 24 24" width={24} height={24}>
                    <Path d={vectors.icons.restoreTrash} fill={iconSelectColor} fillOpacity={iconSelectOpacity}/>
                  </Svg>
                }
              />
            }
            <IconButton
              onPress={selectClear}
              title={t`Clear selection`}
              buttonStyle={styles.button}
              buttonHoverStyle={hoverStyle}
              icon={
                <Svg viewBox="0 0 14 14" width={14} height={14}>
                  <Path d={vectors.icons.close} fill={iconSelectColor} fillOpacity={iconSelectOpacity}/>
                </Svg>
              }
            />
            </View>
          }
          {isModeTrash &&
            <View style={styles.actions}>
              <View style={styles.spacer}/>
              <IconButton
                onPress={modalPurge.open}
                title={t`Purge all files and folders`}
                text={hasFullText ? t`Empty Trash`.toUpperCase() : ''}
                textStyle={classes.buttonText}
                buttonStyle={styles.button}
                buttonHoverStyle={styles.buttonTrashHover}
                icon={
                  <Svg viewBox="0 0 24 24" width={24} height={24}>
                    <Path d={vectors.icons.emptyTrash} fill={colors.neutral.white}/>
                  </Svg>
                }
              />
              <IconButton
                onPress={restore}
                title={t`Restore all files and folders`}
                text={hasFullText ? t`Restore all`.toUpperCase() : ''}
                textStyle={classes.buttonText}
                buttonStyle={styles.button}
                buttonHoverStyle={styles.buttonTrashHover}
                icon={
                  <Svg viewBox="0 0 24 24" width={24} height={24}>
                    <Path d={vectors.icons.restoreTrash} fill={colors.neutral.white}/>
                  </Svg>
                }
              />
              {hasMenu &&
                <IconButton
                  onPress={openMenu}
                  title={t`Open main menu`}
                  buttonStyle={styles.button}
                  buttonHoverStyle={styles.buttonTrashHover}
                  icon={
                    <Svg viewBox="0 0 18 12" width={18} height={12}>
                      <Path d={vectors.icons.menu} fill={colors.neutral.white}/>
                    </Svg>
                  }
                />
              }
            </View>
          }
          {isModeShared &&
            <View style={styles.actions}>
              <View style={styles.spacer}/>
              {!hasDisplayMenu &&
                <LayoutSelector isShared={true} />
              }
              {isLoggedIn &&
                <Pressable
                  style={styles.primary}
                  onPress={myfiles}>
                  <Text style={styles.signupText}>
                    {t`My Files`.toUpperCase()}
                  </Text>
                </Pressable>
              }
              {!isLoggedIn &&
                <View style={styles.authMenu}>
                  <Pressable
                    style={classes.signup}
                    onPress={() => openPage(`${host}/upgrade`)}
                    // @ts-ignore
                    title={t`Create a MediaFire account`}>
                    <Text style={styles.signupText}>
                      {t`Sign up`.toUpperCase()}
                    </Text>
                  </Pressable>
                  <Pressable
                    style={styles.login}
                    onPress={() => openPage(`${host}/login`)}
                    // @ts-ignore
                    title={t`Log in to your MediaFire account`}>
                    <Text>{t`Log in`.toUpperCase()}</Text>
                  </Pressable>
                </View>
              }
            </View>
          }
        </>}
      </View>
    </SafeAreaView>
  );
}, (prev, next) => isEqual(prev, next));

export const styles = StyleSheet.create({
  root: {
    height: 65,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.app.logo,
    transition: 'background-color 0.15s ease-in-out',
  },
  actions: {
    flex: 1,
    height: 65,
    marginRight: 4,
    flexDirection: 'row',
    alignSelf: 'flex-end',
    alignItems: 'center',
  },
  spacer: {
    flex: 1,
  },
  logo: {
    maxWidth: 800,
    marginLeft: 16,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  brandBy: {
    marginLeft: 6,
    marginBottom: 2,
    fontSize: 11,
    opacity: 0.7,
  },
  button: {
    minWidth: 60,
    paddingHorizontal: 8,
    alignItems: 'center',
    flexDirection: 'row',
  },
  buttonText: {
    color: colors.primary.light,
    fontSize: 13,
  },
  buttonTextSelect: {
    color: colors.neutral.white,
  },
  // Search
  search: {
    flex: 1,
  },
  searchIcon: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 54,
  },
  searchInput: {
    color: colors.neutral.white,
    padding: 9,
    fontSize: 18,
    flex: 1,
  },
  // Select
  rootSelect: {
    backgroundColor: colors.primary.dark,
  },
  selected: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 7,
    marginVertical: 18,
    marginRight: 4,
    marginLeft: 10,
    height: 30,
    borderWidth: 1,
    borderRadius: 18,
    borderColor: colors.primary.shade,
  },
  selectedText: {
    paddingHorizontal: 6,
    color: theme.app.header,
    fontSize: 11,
  },
  // Trash
  rootTrash: {
    backgroundColor: colors.secondary.redOrange,
  },
  buttonTrashHover: {
    backgroundColor: luminance(colors.secondary.redOrange, -0.2),
  },
  // Shared
  rootShared: {
    backgroundColor: colors.neutral.white,
    borderColor: colors.neutral.lighter,
  },
  rootSharedSelect: {
    borderColor: theme.files.listItem.selected,
  },
  authMenu: {
    height: 65,
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'center',
  },
  primary: {
    height: 36,
    marginLeft: 4,
    marginRight: 8,
    paddingHorizontal: 16,
    fontSize: 12,
    lineHeight: 36,
    borderRadius: 3,
    backgroundColor: theme.app.logo,
    justifyContent: 'center',
    color: colors.neutral.white,
  },
  signupText: {
    color: colors.neutral.white,
  },
  login: {
    height: 36,
    marginLeft: 4,
    marginRight: 16,
    paddingHorizontal: 16,
    fontSize: 12,
    lineHeight: 32,
    borderWidth: 2,
    borderRadius: 3,
    borderColor: colors.neutral.lighter,
    backgroundColor: colors.neutral.white,
    justifyContent: 'center',
    color: colors.neutral.mid,
  },
});
